import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import {
  calendarEntryDates,
  danishHolidays,
  formatDate,
  formatMonthYear,
  toIsoDate,
  TV_CALENDAR_KIND_LABELS,
  TV_CALENDAR_KINDS,
  WEEKDAY_SHORT_LABELS,
  type TvCalendarEntry,
  type TvCalendarEntryInput,
  type TvCalendarKind,
  type TvCalendarResponse,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { cn } from '@/lib/cn';
import { Alert, Button, Input, Label, Modal, MultiSelect, Select, Skeleton } from '@/components/ui';

/**
 * The calendar behind utilization: days that are not working days for some or all of the people,
 * though no one is absent - days off OneCode gives on top of the legal holidays, school, courses.
 * Vacation and illness are not here; they come from the absence calendar in Outlook.
 *
 * Which days an entry takes is `calendarEntryDates` from packages/shared - the same function the
 * API counts with - so the month shown here is the month the screen is computed from.
 */

const CALENDAR_KEY = ['admin', 'tv-dashboard', 'calendar'] as const;

const DAY_MS = 86_400_000;

const KIND_CLASS: Record<TvCalendarKind, string> = {
  holiday: 'bg-amber-100 text-amber-900',
  school: 'bg-sky-100 text-sky-900',
  course: 'bg-violet-100 text-violet-900',
  parental_leave: 'bg-rose-100 text-rose-900',
};

/** The form as it is being filled in. */
interface Draft {
  id: string | null;
  kind: TvCalendarKind;
  everyone: boolean;
  user_ids: string[];
  start_date: string;
  end_date: string;
  weekdays: number[];
  note: string;
}

function newDraft(date: string): Draft {
  return {
    id: null,
    kind: 'school',
    everyone: false,
    user_ids: [],
    start_date: date,
    end_date: date,
    weekdays: [],
    note: '',
  };
}

function draftOf(entry: TvCalendarEntry): Draft {
  return {
    id: entry.id,
    kind: entry.kind,
    everyone: entry.user_ids === null,
    user_ids: entry.user_ids ?? [],
    start_date: entry.start_date,
    end_date: entry.end_date,
    weekdays: entry.weekdays,
    note: entry.note ?? '',
  };
}

/** The draft as the API takes it, or the first thing to fix. The API checks the same and more. */
function readDraft(draft: Draft): { input: TvCalendarEntryInput } | { error: string } {
  if (!draft.start_date || !draft.end_date) return { error: 'Vælg første og sidste dag.' };
  if (draft.end_date < draft.start_date) return { error: 'Sidste dag ligger før første dag.' };
  if (!draft.everyone && draft.user_ids.length === 0) {
    return { error: 'Vælg mindst én person - eller alle.' };
  }
  return {
    input: {
      kind: draft.kind,
      user_ids: draft.everyone ? null : draft.user_ids,
      start_date: draft.start_date,
      end_date: draft.end_date,
      weekdays: [...draft.weekdays].sort((a, b) => a - b),
      note: draft.note.trim() || null,
    },
  };
}

function shiftMonth(month: string, delta: number): string {
  const [year, value] = month.split('-').map(Number);
  return new Date(Date.UTC(year ?? 0, (value ?? 1) - 1 + delta, 1)).toISOString().slice(0, 7);
}

/** The days shown for a month: whole weeks, Monday first. */
function monthDays(month: string): string[] {
  const first = Date.parse(`${month}-01T00:00:00Z`);
  const offset = (new Date(first).getUTCDay() + 6) % 7;
  const [year, value] = month.split('-').map(Number);
  const length = new Date(Date.UTC(year ?? 0, value ?? 1, 0)).getUTCDate();
  const cells = Math.ceil((offset + length) / 7) * 7;
  return Array.from({ length: cells }, (_, index) =>
    new Date(first + (index - offset) * DAY_MS).toISOString().slice(0, 10),
  );
}

function sortEntries(entries: TvCalendarEntry[]): TvCalendarEntry[] {
  return [...entries].sort((a, b) => a.start_date.localeCompare(b.start_date));
}

export function TvCalendarModal({
  open,
  onClose,
  onChanged,
}: {
  open: boolean;
  onClose: () => void;
  /** After a change has been saved or rolled back: the utilization figures must be fetched again. */
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  /* 'Se TV-dashboard' uden 'Ændr': kalenderen til at laese - en periode aabnes, men rettes ikke. */
  const canEdit = useAuth().can('tv_dashboard.manage');
  const today = toIsoDate(new Date());
  const [month, setMonth] = useState(today.slice(0, 7));
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const query = useQuery({
    queryKey: CALENDAR_KEY,
    queryFn: () => api.get<TvCalendarResponse>('/tv-dashboard/calendar'),
    enabled: open,
  });

  const users = query.data?.users ?? [];
  const nameOf = useMemo(() => new Map(users.map((user) => [user.id, user.name])), [users]);
  const days = useMemo(() => monthDays(month), [month]);

  /** The entries on each day shown. */
  const byDate = useMemo(() => {
    const result = new Map<string, TvCalendarEntry[]>();
    const first = days[0] ?? '';
    const last = days[days.length - 1] ?? '';
    for (const entry of query.data?.entries ?? []) {
      if (entry.end_date < first || entry.start_date > last) continue;
      for (const date of calendarEntryDates(entry)) {
        if (date >= first && date <= last) result.set(date, [...(result.get(date) ?? []), entry]);
      }
    }
    return result;
  }, [days, query.data?.entries]);

  const holidays = useMemo(() => {
    const years = new Set(days.map((date) => Number(date.slice(0, 4))));
    return new Map(
      [...years].flatMap((year) => danishHolidays(year)).map((day) => [day.date, day.name]),
    );
  }, [days]);

  const inMonth = (query.data?.entries ?? []).filter(
    (entry) => entry.start_date <= `${month}-31` && entry.end_date >= `${month}-01`,
  );

  const who = (entry: TvCalendarEntry): string => {
    if (entry.user_ids === null) return 'Alle';
    if (entry.user_ids.length === 1) return nameOf.get(entry.user_ids[0] ?? '') ?? '1 person';
    return `${entry.user_ids.length} personer`;
  };
  const label = (entry: TvCalendarEntry): string =>
    `${who(entry)} · ${entry.note ?? TV_CALENDAR_KIND_LABELS[entry.kind]}`;

  const settle = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: CALENDAR_KEY });
    onChanged();
  };

  /* Optimistic: the calendar shows the change at once and rolls back if the API refuses it. */
  const save = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: TvCalendarEntryInput }) =>
      id
        ? api.put<TvCalendarEntry>(`/tv-dashboard/calendar/${id}`, input)
        : api.post<TvCalendarEntry>('/tv-dashboard/calendar', input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: CALENDAR_KEY });
      const previous = queryClient.getQueryData<TvCalendarResponse>(CALENDAR_KEY);
      const attempted = draft;
      if (previous) {
        const entry: TvCalendarEntry = { id: id ?? `pending-${Date.now()}`, ...input };
        queryClient.setQueryData<TvCalendarResponse>(CALENDAR_KEY, {
          ...previous,
          entries: sortEntries([...previous.entries.filter((other) => other.id !== id), entry]),
        });
      }
      setDraft(null);
      setMessage(null);
      return { previous, attempted };
    },
    onError: (cause, _variables, context) => {
      queryClient.setQueryData(CALENDAR_KEY, context?.previous);
      // What was typed stays in the form, so it can be fixed instead of typed again.
      setDraft(context?.attempted ?? null);
      setMessage(cause instanceof ApiError ? cause.message : 'Kunne ikke gemme perioden.');
    },
    onSettled: settle,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/tv-dashboard/calendar/${id}`),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: CALENDAR_KEY });
      const previous = queryClient.getQueryData<TvCalendarResponse>(CALENDAR_KEY);
      const attempted = draft;
      if (previous) {
        queryClient.setQueryData<TvCalendarResponse>(CALENDAR_KEY, {
          ...previous,
          entries: previous.entries.filter((entry) => entry.id !== id),
        });
      }
      setDraft(null);
      setMessage(null);
      return { previous, attempted };
    },
    onError: (cause, _id, context) => {
      queryClient.setQueryData(CALENDAR_KEY, context?.previous);
      setDraft(context?.attempted ?? null);
      setMessage(cause instanceof ApiError ? cause.message : 'Kunne ikke slette perioden.');
    },
    onSettled: settle,
  });

  const read = draft ? readDraft(draft) : null;
  const edit = (change: Partial<Draft>): void => {
    if (draft) setDraft({ ...draft, ...change });
  };
  const openNew = (date: string): void => {
    if (!canEdit) return;
    setMessage(null);
    setDraft(newDraft(date));
  };
  const openEdit = (entry: TvCalendarEntry): void => {
    // An entry still on its way to the API has no ID to save against yet.
    if (entry.id.startsWith('pending-')) return;
    setMessage(null);
    setDraft(draftOf(entry));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Kalender: fridage, skole og kursus"
      description={`Dage, der ikke er arbejdsdage for personen, men heller ikke fravær. Ferie og sygdom kommer fra fraværskalenderen i Outlook. ${canEdit ? 'Klik på en dag for at tilføje, på en periode for at rette.' : 'Klik på en periode for at se den.'}`}
    >
      <div className="space-y-4">
        {message ? (
          <Alert tone="error" onDismiss={() => setMessage(null)}>
            {message}
          </Alert>
        ) : null}

        {draft ? (
          <div className="space-y-3 rounded-md border border-line p-3">
            <p className="text-sm font-semibold">
              {!canEdit ? 'Periode' : draft.id ? 'Ret periode' : 'Ny periode'}
            </p>
            {/* Uden 'Ændr TV-dashboard' er felterne til at laese. */}
            <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-3 border-0 p-0">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="calendar-kind">Hvad</Label>
                  <Select
                    id="calendar-kind"
                    value={draft.kind}
                    onChange={(event) => {
                      const kind = event.target.value as TvCalendarKind;
                      // A day off is usually for everyone; school and courses for someone.
                      edit(draft.id ? { kind } : { kind, everyone: kind === 'holiday' });
                    }}
                  >
                    {TV_CALENDAR_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {TV_CALENDAR_KIND_LABELS[kind]}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="calendar-who">Hvem</Label>
                  <Select
                    id="calendar-who"
                    value={draft.everyone ? 'all' : 'some'}
                    onChange={(event) => edit({ everyone: event.target.value === 'all' })}
                  >
                    <option value="some">Udvalgte personer</option>
                    <option value="all">Alle</option>
                  </Select>
                  {draft.everyone ? null : (
                    <div className="mt-2">
                      <MultiSelect
                        values={draft.user_ids}
                        onChange={(user_ids) => edit({ user_ids })}
                        allLabel="Vælg personer"
                        options={users.map((user) => ({
                          value: user.id,
                          label: user.active ? user.name : `${user.name} (deaktiveret)`,
                        }))}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="calendar-start">Første dag</Label>
                  <Input
                    id="calendar-start"
                    type="date"
                    value={draft.start_date}
                    onChange={(event) =>
                      edit({
                        start_date: event.target.value,
                        end_date:
                          draft.end_date < event.target.value ? event.target.value : draft.end_date,
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="calendar-end">Sidste dag</Label>
                  <Input
                    id="calendar-end"
                    type="date"
                    value={draft.end_date}
                    min={draft.start_date}
                    onChange={(event) => edit({ end_date: event.target.value })}
                  />
                </div>
              </div>

              <fieldset>
                <legend className="mb-1 text-sm font-medium">Kun på</legend>
                <div className="flex flex-wrap gap-3">
                  {[1, 2, 3, 4, 5].map((weekday) => (
                    <label key={weekday} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-brand"
                        checked={draft.weekdays.includes(weekday)}
                        onChange={(event) =>
                          edit({
                            weekdays: event.target.checked
                              ? [...draft.weekdays, weekday]
                              : draft.weekdays.filter((day) => day !== weekday),
                          })
                        }
                      />
                      {WEEKDAY_SHORT_LABELS[weekday - 1]}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  Ingen valgt = alle hverdage i perioden. Fx skole hver mandag: vælg Man. Weekender
                  og helligdage tæller aldrig.
                </p>
              </fieldset>

              <div>
                <Label htmlFor="calendar-note">Note</Label>
                <Input
                  id="calendar-note"
                  maxLength={200}
                  placeholder="Fx Juleaftensdag eller Skoleperiode 3"
                  value={draft.note}
                  onChange={(event) => edit({ note: event.target.value })}
                />
              </div>
            </fieldset>

            {read && 'error' in read ? <Alert tone="warning">{read.error}</Alert> : null}

            <div className="flex flex-wrap items-center gap-2">
              {canEdit ? (
                <Button
                  disabled={!read || 'error' in read}
                  loading={save.isPending}
                  onClick={() => {
                    if (read && 'input' in read) save.mutate({ id: draft.id, input: read.input });
                  }}
                >
                  Gem
                </Button>
              ) : null}
              {canEdit && draft.id ? (
                <Button
                  variant="destructive"
                  loading={remove.isPending}
                  onClick={() => {
                    if (draft.id) remove.mutate(draft.id);
                  }}
                >
                  Slet
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {canEdit ? 'Annuller' : 'Luk'}
              </Button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Forrige måned"
              onClick={() => setMonth(shiftMonth(month, -1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="w-36 text-center text-sm font-semibold capitalize">
              {formatMonthYear(`${month}-01`)}
            </p>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Næste måned"
              onClick={() => setMonth(shiftMonth(month, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            {month !== today.slice(0, 7) ? (
              <Button variant="ghost" size="sm" onClick={() => setMonth(today.slice(0, 7))}>
                I dag
              </Button>
            ) : null}
          </div>
          {canEdit ? (
            <Button
              size="sm"
              onClick={() => openNew(today.slice(0, 7) === month ? today : `${month}-01`)}
            >
              <Plus className="h-4 w-4" />
              Tilføj periode
            </Button>
          ) : null}
        </div>

        {query.isLoading ? (
          <Skeleton className="h-80" />
        ) : query.error ? (
          <Alert tone="error">Kunne ikke hente kalenderen.</Alert>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border border-line bg-line text-xs">
              {WEEKDAY_SHORT_LABELS.map((weekday) => (
                <div key={weekday} className="bg-canvas px-2 py-1 font-medium text-ink-muted">
                  {weekday}
                </div>
              ))}
              {days.map((date, index) => {
                const outside = !date.startsWith(month);
                const weekend = index % 7 >= 5;
                const holiday = holidays.get(date);
                return (
                  <div
                    key={date}
                    className={cn(
                      'min-h-[5.5rem] min-w-0 p-1',
                      outside || weekend || holiday ? 'bg-canvas' : 'bg-surface',
                      outside && 'text-ink-muted',
                    )}
                  >
                    <button
                      type="button"
                      aria-label={
                        canEdit ? `Tilføj periode fra ${formatDate(date)}` : formatDate(date)
                      }
                      disabled={!canEdit}
                      onClick={() => openNew(date)}
                      className={cn(
                        'rounded px-1 font-medium hover:bg-line',
                        date === today && 'bg-brand text-white hover:bg-brand-700',
                      )}
                    >
                      {Number(date.slice(8))}
                    </button>
                    {holiday ? (
                      <p className="truncate px-1 text-[11px] text-ink-muted" title={holiday}>
                        {holiday}
                      </p>
                    ) : null}
                    <div className="mt-0.5 space-y-0.5">
                      {(byDate.get(date) ?? []).map((entry) => (
                        <button
                          key={entry.id}
                          type="button"
                          title={`${label(entry)}, ${formatDate(entry.start_date)} til ${formatDate(entry.end_date)}`}
                          onClick={() => openEdit(entry)}
                          className={cn(
                            'block w-full truncate rounded px-1 text-left text-[11px]',
                            KIND_CLASS[entry.kind],
                          )}
                        >
                          {label(entry)}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap gap-3 text-xs text-ink-muted">
              {TV_CALENDAR_KINDS.map((kind) => (
                <span key={kind} className="flex items-center gap-1.5">
                  <span className={cn('h-3 w-3 rounded', KIND_CLASS[kind])} aria-hidden />
                  {TV_CALENDAR_KIND_LABELS[kind]}
                </span>
              ))}
              <span>Grå dage er weekend og helligdage - de tæller aldrig som arbejdsdage.</span>
            </div>

            {inMonth.length > 0 ? (
              <div>
                <p className="mb-1 text-sm font-semibold">
                  Perioder i {formatMonthYear(`${month}-01`)}
                </p>
                <ul className="divide-y divide-line rounded-md border border-line text-sm">
                  {inMonth.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        onClick={() => openEdit(entry)}
                        className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2 text-left hover:bg-canvas"
                      >
                        <span>
                          <span
                            className={cn(
                              'mr-2 rounded px-1.5 py-0.5 text-xs',
                              KIND_CLASS[entry.kind],
                            )}
                          >
                            {TV_CALENDAR_KIND_LABELS[entry.kind]}
                          </span>
                          {entry.user_ids === null
                            ? 'Alle'
                            : entry.user_ids
                                .map((id) => nameOf.get(id) ?? 'Ukendt bruger')
                                .join(', ')}
                          {entry.note ? (
                            <span className="text-ink-muted"> · {entry.note}</span>
                          ) : null}
                        </span>
                        <span className="tabular text-xs text-ink-muted">
                          {entry.start_date === entry.end_date
                            ? formatDate(entry.start_date)
                            : `${formatDate(entry.start_date)} til ${formatDate(entry.end_date)}`}
                          {entry.weekdays.length > 0
                            ? ` · kun ${entry.weekdays.map((day) => WEEKDAY_SHORT_LABELS[day - 1]).join(', ')}`
                            : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
